import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';

export interface DraftStep {
  key: string;
  instruction: string;
  isRequired: boolean;
  requirePhoto: boolean;
}

export interface ChecklistStepEditorProps {
  step: DraftStep;
  index: number;
  total: number;
  onChange: (patch: Partial<DraftStep>) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}

/**
 * One editable SOP step.
 *
 * Reordering uses buttons rather than drag-and-drop: this is configured on whatever
 * device is to hand, often a tablet, and a precise drag is exactly what gloves and a
 * cracked screen make unreliable. Buttons also stay operable by keyboard.
 */
export function ChecklistStepEditor({
  step,
  index,
  total,
  onChange,
  onMove,
  onRemove,
}: ChecklistStepEditorProps) {
  const { t } = useTranslation();

  return (
    <li className="rounded-lg border border-slate-800 bg-slate-900 p-3">
      <div className="flex items-start gap-2">
        <span className="mt-2 w-6 shrink-0 text-center text-xs text-slate-500">{index + 1}</span>

        <div className="min-w-0 flex-1">
          <input
            value={step.instruction}
            onChange={(event) => onChange({ instruction: event.target.value })}
            placeholder={t('adminChecklists.instructionPlaceholder')}
            aria-label={t('adminChecklists.stepInstruction', { number: index + 1 })}
            maxLength={500}
            className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-2 text-sm"
          />

          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            <label className="flex items-center gap-1.5 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={step.isRequired}
                onChange={(event) => onChange({ isRequired: event.target.checked })}
                className="size-4 min-h-0"
              />
              {t('adminChecklists.mandatory')}
            </label>

            <label className="flex items-center gap-1.5 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={step.requirePhoto}
                onChange={(event) => onChange({ requirePhoto: event.target.checked })}
                className="size-4 min-h-0"
              />
              {t('adminChecklists.expectPhoto')}
            </label>
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-1">
          <button
            type="button"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            aria-label={t('adminChecklists.moveUp')}
            className="flex size-8 min-h-0 items-center justify-center rounded bg-slate-800 p-0 text-slate-300 disabled:opacity-30"
          >
            <ArrowUp className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            disabled={index === total - 1}
            onClick={() => onMove(1)}
            aria-label={t('adminChecklists.moveDown')}
            className="flex size-8 min-h-0 items-center justify-center rounded bg-slate-800 p-0 text-slate-300 disabled:opacity-30"
          >
            <ArrowDown className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label={t('adminChecklists.removeStep', { number: index + 1 })}
            className="flex size-8 min-h-0 items-center justify-center rounded bg-transparent p-0 text-slate-500"
          >
            <Trash2 className="size-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </li>
  );
}
